'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface ChatConversation {
  id: string;
  type: 'travel' | 'storage';
  otherPartyName: string;
  otherPartyAvatar?: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  referenceId?: string;
  orderId?: string;
  tripId?: string;
  bookingId?: string;
}

function formatTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function MessageCenter() {
  const { user } = useAuth();
  const supabase = createClient();
  const router = useRouter();

  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [activeTab, setActiveTab] = useState<'travel' | 'storage' | 'all'>('all');
  const ref = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);
  useEffect(() => { openRef.current = open; }, [open]);

  const fetchConversations = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const convos: ChatConversation[] = [];

      // ── Travel Chats (order_chats + order_chat_messages) ──────────────────
      const { data: orderChats } = await supabase
        .from('order_chats')
        .select(`
          id,
          order_id,
          trip_id,
          sender_id,
          traveler_id,
          updated_at
        `)
        .or(`sender_id.eq.${user.id},traveler_id.eq.${user.id}`)
        .order('updated_at', { ascending: false })
        .limit(20);

      if (orderChats) {
        for (const chat of orderChats) {
          const { data: lastMsgData } = await supabase
            .from('order_chat_messages')
            .select('content, created_at, sender_id, is_read')
            .eq('chat_id', chat.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          const { count: unreadCount } = await supabase
            .from('order_chat_messages')
            .select('id', { count: 'exact', head: true })
            .eq('chat_id', chat.id)
            .neq('sender_id', user.id)
            .eq('is_read', false);

          const otherPartyId = chat.sender_id === user.id ? chat.traveler_id : chat.sender_id;
          let otherPartyName = 'Unknown';
          let otherPartyAvatar = '';
          if (otherPartyId) {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('full_name, avatar_url')
              .eq('id', otherPartyId)
              .maybeSingle();
            if (profile) {
              otherPartyName = profile.full_name || 'Unknown';
              otherPartyAvatar = profile.avatar_url || '';
            }
          }

          convos.push({
            id: chat.id,
            type: 'travel',
            otherPartyName,
            otherPartyAvatar,
            lastMessage: lastMsgData?.content || 'No messages yet',
            lastMessageAt: lastMsgData?.created_at || chat.updated_at,
            unreadCount: unreadCount ?? 0,
            orderId: chat.order_id,
            tripId: chat.trip_id,
          });
        }
      }

      // ── Storage Booking Chats ─────────────────────────────────────────────
      // Fetch bookings where user is the customer
      const { data: customerBookings } = await supabase
        .from('storage_bookings')
        .select(`
          id,
          listing_id,
          customer_id,
          updated_at,
          storage_listings (
            id,
            name,
            host_id,
            host_name
          )
        `)
        .eq('customer_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(10);

      // Fetch bookings where user is the host (via storage_listings)
      const { data: hostListings } = await supabase
        .from('storage_listings')
        .select('id, name')
        .eq('host_id', user.id);

      const hostListingIds = (hostListings || []).map((l: any) => l.id);
      let hostBookings: any[] = [];
      if (hostListingIds.length > 0) {
        const { data: hb } = await supabase
          .from('storage_bookings')
          .select(`
            id,
            listing_id,
            customer_id,
            updated_at,
            storage_listings (
              id,
              name,
              host_id,
              host_name
            )
          `)
          .in('listing_id', hostListingIds)
          .order('updated_at', { ascending: false })
          .limit(10);
        hostBookings = hb || [];
      }

      const allStorageBookings = [...(customerBookings || []), ...hostBookings];
      // Deduplicate by booking id
      const seenBookingIds = new Set<string>();
      const uniqueBookings = allStorageBookings.filter(b => {
        if (seenBookingIds.has(b.id)) return false;
        seenBookingIds.add(b.id);
        return true;
      });

      for (const booking of uniqueBookings) {
        const chatId = `storage-booking-${booking.id}`;
        const listing = (booking as any).storage_listings;

        // Determine other party
        let otherPartyName = 'Storage User';
        let otherPartyAvatar = '';
        const isCustomer = booking.customer_id === user.id;

        if (isCustomer) {
          // Other party is the host
          otherPartyName = listing?.host_name || 'Storage Host';
          if (listing?.host_id) {
            const { data: hostProfile } = await supabase
              .from('user_profiles')
              .select('full_name, avatar_url')
              .eq('id', listing.host_id)
              .maybeSingle();
            if (hostProfile) {
              otherPartyName = hostProfile.full_name || otherPartyName;
              otherPartyAvatar = hostProfile.avatar_url || '';
            }
          }
        } else {
          // Other party is the customer
          if (booking.customer_id) {
            const { data: customerProfile } = await supabase
              .from('user_profiles')
              .select('full_name, avatar_url')
              .eq('id', booking.customer_id)
              .maybeSingle();
            if (customerProfile) {
              otherPartyName = customerProfile.full_name || 'Customer';
              otherPartyAvatar = customerProfile.avatar_url || '';
            }
          }
        }

        // Fetch messages for this booking chat
        const { data: storageMessages } = await supabase
          .from('delivery_messages')
          .select('content, created_at, sender_id, is_read')
          .eq('delivery_id', chatId)
          .order('created_at', { ascending: false })
          .limit(1);

        const lastMsg = storageMessages?.[0];

        // Count unread
        const { count: unreadCount } = await supabase
          .from('delivery_messages')
          .select('id', { count: 'exact', head: true })
          .eq('delivery_id', chatId)
          .neq('sender_id', user.id)
          .eq('is_read', false);

        convos.push({
          id: chatId,
          type: 'storage',
          otherPartyName,
          otherPartyAvatar,
          lastMessage: lastMsg?.content || `Booking: ${listing?.name || 'Storage Unit'}`,
          lastMessageAt: lastMsg?.created_at || booking.updated_at,
          unreadCount: unreadCount ?? 0,
          referenceId: booking.id,
          bookingId: booking.id,
        });
      }

      // Sort by lastMessageAt desc
      convos.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
      setConversations(convos);
      setTotalUnread(convos.reduce((sum, c) => sum + c.unreadCount, 0));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch only unread count (lightweight) for badge — runs on mount and on realtime events
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      // Count unread travel chat messages
      const { count: travelUnread } = await supabase
        .from('order_chat_messages')
        .select('id', { count: 'exact', head: true })
        .neq('sender_id', user.id)
        .eq('is_read', false);

      // Count unread delivery messages (storage chats)
      const { count: storageUnread } = await supabase
        .from('delivery_messages')
        .select('id', { count: 'exact', head: true })
        .neq('sender_id', user.id)
        .eq('is_read', false);

      setTotalUnread((travelUnread ?? 0) + (storageUnread ?? 0));
    } catch {
      // silent
    }
  }, [user, supabase]);

  useEffect(() => {
    if (user) {
      fetchConversations();
      fetchUnreadCount();
    }
  }, [user, fetchConversations, fetchUnreadCount]);

  // Realtime: listen for new messages
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`msg-center:${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'order_chat_messages' }, (payload) => {
        // Only count messages from others
        if ((payload.new as any)?.sender_id !== user.id) {
          fetchUnreadCount();
        }
        if (openRef.current) fetchConversations();
        else fetchConversations(); // always refresh so list is ready when opened
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'order_chat_messages' }, () => {
        fetchUnreadCount();
        if (openRef.current) fetchConversations();
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'delivery_messages' }, (payload) => {
        if ((payload.new as any)?.sender_id !== user.id) {
          fetchUnreadCount();
        }
        if (openRef.current) fetchConversations();
        else fetchConversations();
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'delivery_messages' }, () => {
        fetchUnreadCount();
        if (openRef.current) fetchConversations();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, fetchConversations, fetchUnreadCount]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleConversationClick = (conv: ChatConversation) => {
    setOpen(false);
    if (conv.type === 'travel') {
      router.push(`/delivery-messaging?chat=${conv.id}`);
    } else {
      // Navigate to storage provider page with booking context
      router.push(`/storage-provider?booking=${conv.bookingId || ''}`);
    }
  };

  const filtered = activeTab === 'all' ? conversations : conversations.filter(c => c.type === activeTab);

  return (
    <div className="relative" ref={ref}>
      {/* Messages button */}
      <button
        onClick={() => { setOpen(v => !v); if (!open) fetchConversations(); }}
        className={`relative p-2.5 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ${
          open ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
        aria-label={`Messages${totalUnread > 0 ? `, ${totalUnread} unread` : ''}`}
        title="Messages"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
        {totalUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none shadow-sm">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-screen max-w-[360px] sm:w-[360px] bg-card border border-border rounded-2xl shadow-xl z-50 overflow-hidden flex flex-col"
          style={{ maxHeight: 'calc(100vh - 80px)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm text-foreground">Messages</h3>
              {totalUnread > 0 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none">
                  {totalUnread}
                </span>
              )}
            </div>
            <button
              onClick={() => { setOpen(false); router.push('/delivery-messaging'); }}
              className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Open full chat →
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-4 pb-2 flex-shrink-0">
            {(['all', 'travel', 'storage'] as const).map((tab) => {
              const count = tab === 'all' ? totalUnread : conversations.filter(c => c.type === tab).reduce((s, c) => s + c.unreadCount, 0);
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize ${
                    activeTab === tab ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {tab === 'travel' ? 'Travel Chats' : tab === 'storage' ? 'Storage Chats' : 'All'}
                  {count > 0 && <span className="ml-1 opacity-80">({count})</span>}
                </button>
              );
            })}
          </div>

          <div className="border-t border-border flex-shrink-0" />

          {/* Conversation list */}
          <div className="overflow-y-auto divide-y divide-border/60" style={{ maxHeight: '380px' }}>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <svg className="w-5 h-5 animate-spin text-primary/60" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <svg className="w-6 h-6 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-foreground">No conversations yet</p>
                <p className="text-xs text-muted-foreground text-center px-6">
                  {activeTab === 'travel' ? 'Travel chats appear when you have active deliveries.' :
                   activeTab === 'storage'? 'Storage chats appear when you have storage bookings.' : 'Your conversations will appear here.'}
                </p>
              </div>
            ) : (
              filtered.map((conv) => (
                <button
                  key={`${conv.type}-${conv.id}`}
                  onClick={() => handleConversationClick(conv)}
                  className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left ${
                    conv.unreadCount > 0 ? 'bg-primary/[0.04]' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-primary/10">
                    {conv.otherPartyAvatar ? (
                      <img src={conv.otherPartyAvatar} alt={conv.otherPartyName} className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-foreground truncate">{conv.otherPartyName}</span>
                      <span className="text-[10px] text-muted-foreground flex-shrink-0">{formatTime(conv.lastMessageAt)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                        conv.type === 'travel' ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'
                      }`}>
                        {conv.type === 'travel' ? 'Travel' : 'Storage'}
                      </span>
                      <p className="text-xs text-muted-foreground truncate">{conv.lastMessage}</p>
                    </div>
                  </div>

                  {/* Unread badge */}
                  {conv.unreadCount > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none flex-shrink-0 mt-1">
                      {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border px-4 py-2.5 flex-shrink-0">
            <button
              onClick={() => { setOpen(false); router.push('/delivery-messaging'); }}
              className="w-full text-xs text-center text-primary hover:text-primary/80 font-medium transition-colors"
            >
              View all messages →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
